import { useCallback, useEffect, useRef, useState, type RefCallback } from 'react';
import { useSelector } from 'react-redux';

import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { getCheckedInConsumerId, getDealXgId, getDealerMMDId } from '@makemydeal/dr-sales-checkin-store';

import { type PreloaderStatus } from './CustomerCardPreloader.types';

export const useCustomerCardPreloader = () => {
    const [status, setStatus] = useState<PreloaderStatus>('idle');
    const loadStartRef = useRef<number | null>(null);

    const commonConsumerId = useSelector(getCheckedInConsumerId);
    const dealId = useSelector(getDealXgId);
    const dealerId = useSelector(getDealerMMDId);

    const environment = offerSessionUtils.getEnvironmentType();

    if (commonConsumerId && loadStartRef.current === null) {
        loadStartRef.current = performance.now();
    }

    useEffect(() => {
        if (commonConsumerId && status === 'idle') {
            setStatus('loading');
            logNewRelicPageAction('SCI:CustomerCardPreloader - Status', {
                dealerId: String(dealerId),
                dealXgId: dealId,
                status: 'loading',
                source: 'buyer_seeded'
            });
        }
    }, [commonConsumerId, dealId, dealerId, status]);

    const handleReady = useCallback(() => {
        const durationMs = Math.round(performance.now() - loadStartRef.current!);
        loadStartRef.current = null;
        setStatus('ready');
        logNewRelicPageAction('SCI:CustomerCardPreloader - Status', {
            dealerId: String(dealerId),
            dealXgId: dealId,
            status: 'ready',
            source: 'module_loaded'
        });
        logNewRelicPageAction('SCI:CustomerCardPreloader - Load Duration', {
            dealerId: String(dealerId),
            dealXgId: dealId,
            durationMs
        });
    }, [dealId, dealerId]);

    const readyRef: RefCallback<HTMLSpanElement> = useCallback(
        (el) => {
            if (el) handleReady();
        },
        [handleReady]
    );

    const handleError = useCallback(() => {
        loadStartRef.current = null;
        setStatus('error');
        logNewRelicPageAction('SCI:CustomerCardPreloader - Status', {
            dealerId: String(dealerId),
            dealXgId: dealId,
            status: 'error',
            source: 'module_load_failure'
        });
    }, [dealId, dealerId]);

    return { commonConsumerId, environment, status, readyRef, handleError };
};
